/**
 * Components — the worker catalog. One ComponentManifest per worker binary
 * per platform/arch (cpu-miner, gpu-miner, inference, …): where the desktop
 * downloads it from, its hash and version, the gates that decide which
 * devices get it (enabled, gpuRequirement, minVramMb) and its pool config.
 *
 * Edits apply to the next miner-config poll (≤5 min) on every install — no
 * deploy. The VRAM floor that keeps 4 GB cards off KawPow (2026-10-24) is the
 * kind of change this screen exists for.
 */
import { useEffect, useMemo, useState } from 'react';
import { Plus, Save, Trash2, X } from 'lucide-react';
import {
  ARCHS,
  COMPONENT_TYPES,
  CONFIG_FORMATS,
  ComponentCreate,
  ComponentEdit,
  ComponentManifest,
  GPU_REQUIREMENTS,
  PLATFORMS
} from '@/api/components';
import {
  useComponents,
  useCreateComponent,
  useDeleteComponent,
  useUpdateComponent
} from '@/hooks/useComponents';
import { useInfraComponents } from '@/hooks/useAdminInfra';
import {
  ActionButton,
  Card,
  SectionLabel,
  Select,
  Skeleton,
  StatCard
} from '@/components/ui/atoms';
import { fmtNum, timeAgo } from '@/lib/format';

const TYPE_COLOR: Record<string, string> = {
  'cpu-miner': 'bg-sky-500/20 text-sky-300 ring-sky-500/30',
  'gpu-miner': 'bg-emerald-500/20 text-emerald-300 ring-emerald-500/30',
  inference: 'bg-fuchsia-500/20 text-fuchsia-300 ring-fuchsia-500/30',
  'bandwidth-relay': 'bg-amber-500/20 text-amber-300 ring-amber-500/30',
  'storage-node': 'bg-indigo-500/20 text-indigo-300 ring-indigo-500/30',
  'cache-edge': 'bg-teal-500/20 text-teal-300 ring-teal-500/30',
  shell: 'bg-white/10 text-nurev-muted ring-white/10'
};

const INPUT =
  'mt-1 w-full rounded-lg border border-nurev-border bg-nurev-surface-lift px-3 py-1.5 font-mono text-xs text-nurev-text outline-none focus:border-nurev-border-strong';

type Filter = 'all' | (typeof PLATFORMS)[number];

const errMsg = (e: unknown): string => {
  const r = e as { response?: { data?: { error?: string; errors?: { msg?: string } } } };
  return r?.response?.data?.error || r?.response?.data?.errors?.msg || 'Request failed.';
};

export function ComponentsScreen() {
  const { data: docs = [], isLoading, error } = useComponents();
  const { data: live = [] } = useInfraComponents();
  const [platform, setPlatform] = useState<Filter>('all');
  const [editing, setEditing] = useState<ComponentManifest | null>(null);
  const [creating, setCreating] = useState(false);

  const sessionsById = useMemo(() => {
    const m: Record<string, { sessions: number; speed: number }> = {};
    live.forEach(c => {
      m[c._id] = { sessions: c.activeSessions || 0, speed: c.liveHashrate || 0 };
    });
    return m;
  }, [live]);

  const rows = useMemo(
    () => docs.filter(d => platform === 'all' || d.platform === platform),
    [docs, platform]
  );
  const enabled = docs.filter(d => d.enabled).length;
  const liveSessions = live.reduce((s, c) => s + (c.activeSessions || 0), 0);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-nurev-text">Components</h1>
          <p className="mt-1 text-sm text-nurev-muted">
            The worker catalog: which binary each platform downloads, and the
            gates that decide which devices get it. Edits reach every install on
            its next config poll (≤5 min).
          </p>
        </div>
        <ActionButton onClick={() => setCreating(true)}>
          <span className="inline-flex items-center gap-1.5">
            <Plus size={14} /> New component
          </span>
        </ActionButton>
      </div>

      {error && (
        <Card className="text-sm text-rose-300">
          Couldn’t load components. Check that nurev-api is reachable.
        </Card>
      )}

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard label="Manifests" value={docs.length} loading={isLoading} />
        <StatCard
          label="Enabled"
          value={enabled}
          sub={`${docs.length - enabled} disabled`}
          loading={isLoading}
        />
        <StatCard label="Live sessions" value={fmtNum(liveSessions)} />
        <StatCard
          label="Platforms"
          value={new Set(docs.map(d => d.platform)).size}
          loading={isLoading}
        />
      </div>

      <div className="flex items-center gap-3">
        <SectionLabel>Catalog</SectionLabel>
        <div className="-mt-4">
          <Select<Filter>
            value={platform}
            onChange={setPlatform}
            options={[
              { value: 'all', label: 'All platforms' },
              ...PLATFORMS.map(p => ({ value: p, label: p }))
            ]}
          />
        </div>
      </div>

      <Card className="overflow-hidden p-0">
        {isLoading ? (
          <div className="p-5">
            <Skeleton className="h-40 w-full" />
          </div>
        ) : rows.length === 0 ? (
          <p className="p-5 text-sm text-nurev-muted">No components.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-nurev-muted">
                  <th className="px-4 py-3 font-medium">Name</th>
                  <th className="py-3 font-medium">Type</th>
                  <th className="py-3 font-medium">Platform</th>
                  <th className="py-3 font-medium">Version</th>
                  <th className="py-3 font-medium">Algorithm</th>
                  <th className="py-3 font-medium">GPU</th>
                  <th className="py-3 text-right font-medium">Min VRAM</th>
                  <th className="py-3 text-right font-medium">Sessions</th>
                  <th className="py-3 font-medium">Enabled</th>
                  <th className="py-3 font-medium">Updated</th>
                  <th className="px-4 py-3 text-right font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-nurev-border">
                {rows.map(c => (
                  <Row
                    key={c._id}
                    c={c}
                    live={sessionsById[c._id]}
                    onEdit={() => setEditing(c)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {editing && (
        <EditDrawer
          key={editing._id}
          c={editing}
          onClose={() => setEditing(null)}
        />
      )}
      {creating && <CreateDrawer onClose={() => setCreating(false)} />}
    </div>
  );
}

// ─── Row ──────────────────────────────────────────────────────────────────

function Row({
  c,
  live,
  onEdit
}: {
  c: ComponentManifest;
  live?: { sessions: number; speed: number };
  onEdit: () => void;
}) {
  const update = useUpdateComponent();
  const toggle = () => update.mutate({ id: c._id, body: { enabled: !c.enabled } });
  return (
    <tr className="hover:bg-white/[0.02]">
      <td className="px-4 py-2.5">
        <p className="font-medium text-nurev-text">{c.name}</p>
        {c.binaryName && (
          <p className="font-mono text-[10px] text-nurev-muted">{c.binaryName}</p>
        )}
      </td>
      <td className="py-2.5">
        <span
          className={`inline-block rounded px-2 py-0.5 text-[10px] font-medium ring-1 ${
            TYPE_COLOR[c.type] || TYPE_COLOR.shell
          }`}
        >
          {c.type}
        </span>
      </td>
      <td className="py-2.5 text-xs text-nurev-muted">
        {c.platform}/{c.arch}
      </td>
      <td className="py-2.5 font-mono text-xs text-nurev-text">{c.version}</td>
      <td className="py-2.5 text-xs text-nurev-muted">{c.algorithm || '—'}</td>
      <td className="py-2.5 text-xs text-nurev-muted">{c.gpuRequirement || '—'}</td>
      <td className="py-2.5 text-right font-mono text-xs text-nurev-text">
        {c.minVramMb ? `${fmtNum(c.minVramMb)} MB` : '—'}
      </td>
      <td className="py-2.5 text-right font-mono text-xs text-nurev-text">
        {live?.sessions ?? 0}
      </td>
      <td className="py-2.5">
        <button
          onClick={toggle}
          disabled={update.isPending}
          title={c.enabled ? 'Enabled — click to disable' : 'Disabled — click to enable'}
          className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors disabled:opacity-50 ${
            c.enabled ? 'bg-emerald-500/70' : 'bg-white/15'
          }`}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
              c.enabled ? 'translate-x-4' : 'translate-x-0.5'
            }`}
          />
        </button>
      </td>
      <td className="py-2.5 text-xs text-nurev-muted">{timeAgo(c.updatedAt)}</td>
      <td className="px-4 py-2.5 text-right">
        <ActionButton size="sm" variant="neutral" onClick={onEdit}>
          Edit
        </ActionButton>
      </td>
    </tr>
  );
}

// ─── Shared form fields ───────────────────────────────────────────────────

interface FormState {
  name: string;
  version: string;
  binaryUrl: string;
  binaryHash: string;
  binarySize: string;
  binaryName: string;
  archive: boolean;
  entrypoint: string;
  algorithm: string;
  configFormat: string;
  gpuRequirement: string;
  minVramMb: string;
  minShellVersion: string;
  config: string;
}

const toForm = (c?: Partial<ComponentManifest>): FormState => ({
  name: c?.name || '',
  version: c?.version || '',
  binaryUrl: c?.binaryUrl || '',
  binaryHash: c?.binaryHash || '',
  binarySize: c?.binarySize ? String(c.binarySize) : '0',
  binaryName: c?.binaryName || '',
  archive: !!c?.archive,
  entrypoint: c?.entrypoint || '',
  algorithm: c?.algorithm || '',
  configFormat: c?.configFormat || '',
  gpuRequirement: c?.gpuRequirement || '',
  minVramMb: c?.minVramMb ? String(c.minVramMb) : '0',
  minShellVersion: c?.minShellVersion || '0.0.0',
  config: JSON.stringify(c?.config ?? {}, null, 2)
});

/** Form → PATCH body. Returns an error string instead of a body when invalid. */
function toEdit(f: FormState): ComponentEdit | string {
  let config: Record<string, unknown>;
  try {
    config = JSON.parse(f.config || '{}');
    if (!config || typeof config !== 'object' || Array.isArray(config)) {
      return 'Config must be a JSON object.';
    }
  } catch {
    return 'Config is not valid JSON.';
  }
  const minVramMb = Number(f.minVramMb || 0);
  const binarySize = Number(f.binarySize || 0);
  if (!Number.isFinite(minVramMb) || minVramMb < 0) return 'Min VRAM must be ≥ 0.';
  if (!Number.isFinite(binarySize) || binarySize < 0) return 'Binary size must be ≥ 0.';
  if (f.archive && !f.entrypoint.trim()) return 'An archive needs an entrypoint.';
  return {
    name: f.name.trim(),
    version: f.version.trim(),
    binaryUrl: f.binaryUrl.trim(),
    binaryHash: f.binaryHash.trim(),
    binarySize,
    binaryName: f.binaryName.trim(),
    archive: f.archive,
    entrypoint: f.entrypoint.trim(),
    algorithm: f.algorithm.trim(),
    configFormat: f.configFormat,
    gpuRequirement: f.gpuRequirement,
    minVramMb,
    minShellVersion: f.minShellVersion.trim() || '0.0.0',
    config
  };
}

function Field({
  label,
  hint,
  children,
  wide
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
  wide?: boolean;
}) {
  return (
    <label className={`block ${wide ? 'sm:col-span-2' : ''}`}>
      <span className="text-xs font-medium text-nurev-muted">{label}</span>
      {children}
      {hint && <span className="mt-1 block text-[11px] text-nurev-muted">{hint}</span>}
    </label>
  );
}

function ManifestFields({
  f,
  set
}: {
  f: FormState;
  set: (patch: Partial<FormState>) => void;
}) {
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <Field label="Name" hint="The component id the desktop reports (e.g. nicehash-kawpow-gpu-nvidia).">
        <input className={INPUT} value={f.name} onChange={e => set({ name: e.target.value })} />
      </Field>
      <Field label="Version">
        <input className={INPUT} value={f.version} onChange={e => set({ version: e.target.value })} />
      </Field>
      <Field label="Binary URL" wide>
        <input className={INPUT} value={f.binaryUrl} onChange={e => set({ binaryUrl: e.target.value })} />
      </Field>
      <Field label="Binary hash" hint="sha256:<hex> — the desktop refuses a download that does not match." wide>
        <input className={INPUT} value={f.binaryHash} onChange={e => set({ binaryHash: e.target.value })} />
      </Field>
      <Field label="Binary name" hint="On-disk name, or the .zip filename for archives.">
        <input className={INPUT} value={f.binaryName} onChange={e => set({ binaryName: e.target.value })} />
      </Field>
      <Field label="Binary size (bytes)" hint="0 = unknown.">
        <input className={INPUT} type="number" min={0} value={f.binarySize} onChange={e => set({ binarySize: e.target.value })} />
      </Field>
      <label className="flex items-center gap-2 text-xs text-nurev-text">
        <input type="checkbox" checked={f.archive} onChange={e => set({ archive: e.target.checked })} />
        Archive (.zip — extract, then run the entrypoint)
      </label>
      <Field label="Entrypoint" hint="Archive only: exe path inside the extracted folder.">
        <input className={INPUT} value={f.entrypoint} onChange={e => set({ entrypoint: e.target.value })} />
      </Field>
      <Field label="Algorithm" hint="randomx, kawpow, …">
        <input className={INPUT} value={f.algorithm} onChange={e => set({ algorithm: e.target.value })} />
      </Field>
      <Field label="Config format">
        <div className="mt-1">
          <Select
            value={f.configFormat}
            onChange={v => set({ configFormat: v })}
            options={CONFIG_FORMATS.map(v => ({ value: v, label: v || '(none)' }))}
            className="w-full"
          />
        </div>
      </Field>
      <Field label="GPU requirement" hint="Empty = CPU component.">
        <div className="mt-1">
          <Select
            value={f.gpuRequirement}
            onChange={v => set({ gpuRequirement: v })}
            options={GPU_REQUIREMENTS.map(v => ({ value: v, label: v || '(none)' }))}
            className="w-full"
          />
        </div>
      </Field>
      <Field label="Min VRAM (MB)" hint="0 = no floor. KawPow's DAG needs 6144 (≈5.8 GB, Oct 2026).">
        <input className={INPUT} type="number" min={0} step={256} value={f.minVramMb} onChange={e => set({ minVramMb: e.target.value })} />
      </Field>
      <Field label="Min shell version">
        <input className={INPUT} value={f.minShellVersion} onChange={e => set({ minShellVersion: e.target.value })} />
      </Field>
      <Field label="Config (JSON)" hint="Pools, worker name, intensity… passed to the worker as-is." wide>
        <textarea className={`${INPUT} min-h-[160px]`} spellCheck={false} value={f.config} onChange={e => set({ config: e.target.value })} />
      </Field>
    </div>
  );
}

// ─── Drawer shell ─────────────────────────────────────────────────────────

function Drawer({
  title,
  subtitle,
  onClose,
  children
}: {
  title: string;
  subtitle?: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-40 flex justify-end bg-black/60" onClick={onClose}>
      <div
        className="h-full w-full max-w-2xl overflow-y-auto border-l border-nurev-border bg-nurev-surface p-6 shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        <div className="mb-5 flex items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-nurev-text">{title}</h2>
            {subtitle && <p className="mt-0.5 font-mono text-[11px] text-nurev-muted">{subtitle}</p>}
          </div>
          <button onClick={onClose} className="rounded p-1 text-nurev-muted hover:bg-white/10" aria-label="Close">
            <X size={16} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

// ─── Edit ─────────────────────────────────────────────────────────────────

function EditDrawer({ c, onClose }: { c: ComponentManifest; onClose: () => void }) {
  const [f, setF] = useState<FormState>(() => toForm(c));
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [formErr, setFormErr] = useState<string | null>(null);
  const update = useUpdateComponent();
  const remove = useDeleteComponent();
  const set = (patch: Partial<FormState>) => setF(prev => ({ ...prev, ...patch }));

  const onSave = () => {
    const body = toEdit(f);
    if (typeof body === 'string') {
      setFormErr(body);
      return;
    }
    setFormErr(null);
    update.mutate({ id: c._id, body }, { onSuccess: onClose });
  };
  const onDelete = () => remove.mutate(c._id, { onSuccess: onClose });

  return (
    <Drawer title={c.name} subtitle={`${c.type} · ${c.platform}/${c.arch} · ${c._id}`} onClose={onClose}>
      <ManifestFields f={f} set={set} />
      <div className="mt-6 flex flex-wrap items-center gap-3">
        <ActionButton onClick={onSave} disabled={update.isPending}>
          <span className="inline-flex items-center gap-1.5">
            <Save size={14} /> {update.isPending ? 'Saving…' : 'Save'}
          </span>
        </ActionButton>
        <ActionButton variant="neutral" onClick={onClose}>Cancel</ActionButton>
        <span className="flex-1" />
        {confirmDelete ? (
          <span className="inline-flex items-center gap-2 text-xs text-rose-300">
            Delete this manifest? Installs lose the component on their next poll.
            <ActionButton size="sm" variant="destructive" onClick={onDelete} disabled={remove.isPending}>
              {remove.isPending ? 'Deleting…' : 'Yes, delete'}
            </ActionButton>
            <ActionButton size="sm" variant="neutral" onClick={() => setConfirmDelete(false)}>No</ActionButton>
          </span>
        ) : (
          <ActionButton size="sm" variant="destructive" onClick={() => setConfirmDelete(true)}>
            <span className="inline-flex items-center gap-1"><Trash2 size={13} /> Delete</span>
          </ActionButton>
        )}
      </div>
      {(formErr || update.isError || remove.isError) && (
        <p className="mt-3 text-xs text-rose-300">
          {formErr || errMsg(update.error || remove.error)}
        </p>
      )}
    </Drawer>
  );
}

// ─── Create ───────────────────────────────────────────────────────────────

function CreateDrawer({ onClose }: { onClose: () => void }) {
  const [type, setType] = useState<string>('gpu-miner');
  const [platform, setPlatform] = useState<string>('windows');
  const [arch, setArch] = useState<string>('x86_64');
  const [f, setF] = useState<FormState>(() => toForm());
  const [formErr, setFormErr] = useState<string | null>(null);
  const create = useCreateComponent();
  const set = (patch: Partial<FormState>) => setF(prev => ({ ...prev, ...patch }));

  const onCreate = () => {
    const body = toEdit(f);
    if (typeof body === 'string') {
      setFormErr(body);
      return;
    }
    if (!body.binaryUrl) {
      setFormErr('Binary URL is required.');
      return;
    }
    setFormErr(null);
    const payload: ComponentCreate = {
      ...body,
      type,
      platform,
      arch,
      binaryUrl: body.binaryUrl,
      enabled: false
    };
    create.mutate(payload, { onSuccess: onClose });
  };

  return (
    <Drawer title="New component" subtitle="Created disabled — enable it from the catalog once the binary and hash are verified." onClose={onClose}>
      <div className="mb-4 grid gap-4 sm:grid-cols-3">
        <Field label="Type">
          <div className="mt-1">
            <Select value={type} onChange={setType} options={COMPONENT_TYPES.map(v => ({ value: v, label: v }))} className="w-full" />
          </div>
        </Field>
        <Field label="Platform">
          <div className="mt-1">
            <Select value={platform} onChange={setPlatform} options={PLATFORMS.map(v => ({ value: v, label: v }))} className="w-full" />
          </div>
        </Field>
        <Field label="Arch">
          <div className="mt-1">
            <Select value={arch} onChange={setArch} options={ARCHS.map(v => ({ value: v, label: v }))} className="w-full" />
          </div>
        </Field>
      </div>
      <ManifestFields f={f} set={set} />
      <div className="mt-6 flex items-center gap-3">
        <ActionButton onClick={onCreate} disabled={create.isPending}>
          {create.isPending ? 'Creating…' : 'Create (disabled)'}
        </ActionButton>
        <ActionButton variant="neutral" onClick={onClose}>Cancel</ActionButton>
      </div>
      {(formErr || create.isError) && (
        <p className="mt-3 text-xs text-rose-300">{formErr || errMsg(create.error)}</p>
      )}
      <p className="mt-3 text-[11px] text-nurev-muted">
        One manifest per (type, platform, arch): a duplicate is refused — edit the existing row instead.
      </p>
    </Drawer>
  );
}
